import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { injectFollowUpWording } from '@modules/common/utils/follow-up-wording';
import { PURGE_REFUSED_REASON } from '@modules/common/pipes/can-purge.pipe';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { AlertService } from '@modules/alert/services/alert.service';
// Common
import { HttpErrorResponse } from '@angular/common/http';
import { Component, effect, inject, Injector, Input, type OnInit, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { type RailMenuItem, type RailScope } from '@modules/common/components/rail-menu/rail-menu';
import { ITEM_FOLLOW_SAVED, itemFollowErrorMessage } from '@modules/common/services/item-follow/item-follow-messages';
import {
  inboxVolumeMenu,
  MAIL_VOLUME_INBOX_SAVED_TEXT,
  MAIL_VOLUME_SAVED_TEXT,
  MAIL_VOLUME_TEAM_SAVED_TEXT,
  MAIL_VOLUME_TEAM_UPDATING_TEXT,
  mailVolumeFromEntry,
  mailRowMenu,
  mailVolumeMenu,
  nextMailVolume,
  teamMailRowMenu,
  teamMailVolumeMenu,
} from './mail-rail-menu';

// Utils
import { Like } from '@modules/common/types/like';
import { checkExhaustiveness } from '@modules/common/utils/switch';

// Types
import { Application } from '@modules/common/types/application';
import {
  MAIL_SETTINGS_WINDOW_PATH,
  SettingsWindowOpenerService,
} from '@modules/settings-window/settings-window-opener.service';
import { Message } from '@modules/messages/types/message';
import { MessageFolder } from '@modules/messages/types/message-folder';
import { MessageFoldersFilters } from '@modules/messages/types/message-folders-filters';
import { MessagesFilters } from '@modules/messages/types/messages-filters';
import { VirtualFolder } from '@modules/messages/types/virtual-folder';

// Services
import { MessageFoldersService } from '@modules/messages/services/message-folders.service';
import { MessagesService } from '@modules/messages/services/messages.service';
import { MailAppSettingsService } from '@modules/messages/services/settings.service';

// Components
import { BaseSidebarContextMenuComponent } from '@modules/common/components/base-sidebar-context-menu/base-sidebar-context-menu.component';
import { MailVolume } from '@sharedlib/common/types/mail-app/mail-volume';
import { StitchType } from '@sharedlib/common/types/StitchType';

// RX
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/** What a refused or failed Mail volume save tells the member: the server's own words when it gave any. */
function mailVolumeErrorMessage(error: unknown): string {
  const message = error instanceof HttpErrorResponse && typeof error.error?.message === 'string' ? error.error.message : '';
  return message || 'Mail volume could not be saved.';
}

@Component({
  selector: 'app-sidebar-context-menu',
  templateUrl: './sidebar-context-menu.component.html',
  styleUrls: ['../../../common/components/base-sidebar-context-menu/base-sidebar-context-menu.component.less'],
  standalone: false,
})
export class SidebarContextMenuComponent
  extends BaseSidebarContextMenuComponent<StitchType.MESSAGE, StitchType.MESSAGE_FOLDER, VirtualFolder>
  implements OnInit
{
  protected readonly followUpWording = injectFollowUpWording();
  protected readonly PURGE_REFUSED_REASON = PURGE_REFUSED_REASON;

  /**
   * A team workspace's Inbox rows offer Mail volume to team logins only. The personal rail's Inbox offers it to every
   * login (SBUX-1183): it is the personal mailbox's own setting, which the Settings window offers them too.
   */
  private readonly accountKind = inject(AccountKindService);
  /**
   * A My Workspace or Team Workspace row, or the personal rail's Inbox: the shared rail menu (`mail-rail-menu.ts`).
   * Other rows keep the menu below.
   */
  @Input() public rail = false;
  /** Whether this shared menu belongs to the member mailbox or Team Workspace's mailbox. */
  @Input() public railScope: RailScope = 'mine';
  /** Shows a view of the clicked row's mailbox in the rail ("Open Other Mail"). */
  @Input() public openView: ((filter: VirtualFolder) => void) | undefined;

  /** The saved level: what this client last loaded or saved for the mailbox, drawn at once; `undefined` only before the first load. */
  protected readonly railMailVolume = signal<MailVolume | undefined>(undefined);
  private readonly injector = inject(Injector);
  private readonly railRouter = inject(Router);

  advancedSearchStateKey = Application.mail;

  /** Set once the inputs the volume reads depend on are in place (`ngOnInit`). */
  private mailVolumeReady = false;
  private mailVolumeStarted = false;
  /** "New folder" on a rail row: a plain folder in the row's mailbox, never the row's flag, snooze or follow-up. */
  protected railNewFolder = new MessageFolder();

  constructor(
    private messagesService: MessagesService,
    private messageFoldersService: MessageFoldersService,
    private settingsService: MailAppSettingsService,
  ) {
    super(messagesService, messageFoldersService);
    // A session stored before the kind existed learns it after the menu can already be open: start the volume then.
    effect(() => {
      this.accountKind.isTeam();
      untracked(() => this.startMailVolume());
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.rail) this.railNewFolder = new MessageFolder({ accountId: this.tenantId });
    this.mailVolumeReady = true;
    this.startMailVolume();
  }

  /**
   * The personal rail's Inbox row: no tenant, because it is the signed-in account's own mailbox (the flat rail binds
   * `team?.id`, which names none there). A workspace row always names its mailbox.
   */
  protected get personalInbox(): boolean {
    return this.rail && this.railScope === 'mine' && !this.tenantId && this.sidebarFilterKey === 'inbox';
  }

  /**
   * Starts the mail volume, once: at once for the personal Inbox, whatever the login; for a workspace's Inbox rows,
   * as soon as the login is known to be a team login.
   */
  private startMailVolume(): void {
    if (!this.mailVolumeReady || this.mailVolumeStarted) return;
    if (this.personalInbox) {
      this.mailVolumeStarted = true;
      this.railMailVolume.set(this.settingsService.knownMailVolume(null));
      // A refresh, as below; a failed read leaves the remembered ticks drawn, and opening a menu shows no error.
      this.settingsService
        .getMailVolume(null)
        .pipe(takeUntil(this.alive))
        .subscribe({ next: (volume) => this.railMailVolume.set(volume), error: () => undefined });
      return;
    }
    // The old menu (not a rail row) offers no Mail volume, so it reads none.
    if (!this.rail || !this.accountKind.isTeam()) return;
    this.mailVolumeStarted = true;
    if (this.sidebarFilterKey === 'inbox' && this.railScope === 'team' && this.tenantId) {
      // The member's OWN level for the team mailbox (the team-wide row is another thing, on the settings page).
      const team = this.tenantId;
      this.railMailVolume.set(this.settingsService.knownMemberMailVolume(team));
      this.settingsService
        .getMemberMailVolume(team)
        .pipe(takeUntil(this.alive))
        .subscribe(({ mailVolume }) => this.railMailVolume.set(mailVolume));
    }
    if (this.sidebarFilterKey === 'inbox' && this.railScope === 'mine') {
      this.railMailVolume.set(this.settingsService.knownMailVolume(this.tenantId ?? null));
      // A refresh: it corrects the tick only if the server's level differs from what this client knew.
      this.settingsService
        .getMailVolume(this.tenantId ?? null)
        .pipe(takeUntil(this.alive))
        .subscribe((volume) => this.railMailVolume.set(volume));
    }
  }

  /** Opens the Mail settings window over the app (`/mail(settings:mail/permissions)`), not the old settings page. */
  public override openSettings(): void {
    this.injector.get(SettingsWindowOpenerService).openPage(MAIL_SETTINGS_WINDOW_PATH);
  }

  /** `canPurge` comes from the template's purge pipe, which the old Empty trash entry reads too. */
  protected railEntries(canPurge: boolean): readonly RailMenuItem[] | undefined {
    if (!this.rail) return undefined;
    if (this.personalInbox) return inboxVolumeMenu(this.railMailVolume());
    const purgeRefused = canPurge ? undefined : PURGE_REFUSED_REASON;
    const volumeOffered = this.sidebarFilterKey === 'inbox' && this.accountKind.isTeam();
    if (this.railScope === 'team') {
      return volumeOffered
        ? teamMailVolumeMenu(this.railMailVolume())
        : teamMailRowMenu(this.sidebarFilterKey, { purgeRefused });
    }
    if (volumeOffered) return mailVolumeMenu(this.railMailVolume());
    return mailRowMenu(this.sidebarFilterKey, { purgeRefused });
  }

  protected runRailEntry(id: string): void {
    const volume = mailVolumeFromEntry(id);
    if (volume) {
      this.saveMailVolume(volume);
      return;
    }
    switch (id) {
      case 'new-item':
        void this.messagesService.composeMessage(
          this.railScope === 'team'
            ? { injector: this.injector, message: new Message({ accountId: this.tenantId ?? undefined }) }
            : { injector: this.injector },
        );
        return;
      case 'read-all':
      case 'read-all-team':
        this.readAll();
        return;
      case 'clear':
        if (this.sidebarFilterKey === 'following') this.clearMySubscriptions();
        else this.clearRailRow();
        return;
      case 'clear-team':
        this.clearRailRow();
        return;
      case 'empty-trash':
        this.emptyTrash();
        return;
      case 'trash-archive':
        this.confirmTrashArchive();
        return;
      case 'delete-all':
        if (this.railScope !== 'team') this.deleteAll();
        else
          this.confirmRail(
            'Move to trash',
            'Every email in Spam in Team Workspace moves to Trash, for everyone in this team. You can restore them from Trash.',
            'MOVE TO TRASH',
            () => this.deleteAll(),
          );
        return;
      case 'send-all':
        if (this.railScope !== 'team') this.sendAll();
        return;
      case 'open-other':
        this.openView?.('other_mail');
        return;
      case 'volume-settings':
        // the settings page reads the mailbox on screen; make it this member's
        this.openView?.('inbox');
        this.openSettings();
        return;
      case 'settings':
        this.openViewSettings(this.sidebarFilterKey === 'all_folders' ? 'message-folders' : 'messages');
        return;
    }
  }

  /**
   * Not tied to this menu's lifetime: the menu closes on the click, the save must still land. In the team
   * mailbox the level saved is the member's OWN; elsewhere it is the mailbox's.
   */
  private saveMailVolume(clicked: MailVolume): void {
    const previous = this.railMailVolume();
    const next = nextMailVolume(previous ?? MailVolume.EVERYONE, clicked);
    // Resolved now: the menu closes on this click, and the toast follows the PUT.
    const toaster = this.injector.get(ToasterService);
    this.railMailVolume.set(next);
    if (this.railScope === 'team' && this.tenantId) {
      const team = this.tenantId;
      this.settingsService.updateMemberMailVolume(team, next).subscribe({
        next: ({ updating }) => {
          // The list and badges are this member's now: read them again, they were placed for the old level.
          this.messagesService.refreshAfterMemberVolumeChange(team);
          toaster.show({ text: updating ? MAIL_VOLUME_TEAM_UPDATING_TEXT : MAIL_VOLUME_TEAM_SAVED_TEXT });
          // The mail already in the mailbox is placed in the background: read the lists again once it is.
          if (updating) {
            this.settingsService
              .memberMailVolumeSettled(team)
              .subscribe(() => this.messagesService.refreshAfterMemberVolumeChange(team));
          }
        },
        error: (error: unknown) => {
          this.railMailVolume.set(previous);
          toaster.show({ text: mailVolumeErrorMessage(error) });
        },
      });
      return;
    }
    if (this.personalInbox) {
      const refused = (error?: unknown): void => {
        this.railMailVolume.set(previous);
        toaster.show({ text: mailVolumeErrorMessage(error) });
      };
      this.settingsService.updateMailVolume(null, next).subscribe({
        next: (saved) => {
          // `false`: nothing was sent (this login may not change the setting), so nothing was saved.
          if (saved) toaster.show({ text: MAIL_VOLUME_INBOX_SAVED_TEXT });
          else refused();
        },
        error: refused,
      });
      return;
    }
    this.settingsService
      .updateMailVolume(this.tenantId ?? null, next)
      .subscribe(() => toaster.show({ text: MAIL_VOLUME_SAVED_TEXT }));
  }

  /** The prototype's "Review archived items for Trash…" asks first (workspace-menus.js:27); Trash is restorable. */
  private confirmTrashArchive(): void {
    const body =
      this.railScope === 'team'
        ? 'Every archived email and folder in Team Workspace moves to Trash, for everyone in this team. You can restore them from Trash.'
        : 'Every archived email and folder in My Workspace moves to Trash. You can restore them from Trash.';
    this.confirmRail('Move to trash', body, 'MOVE TO TRASH', () => this.deleteAll());
  }

  private confirmRail(title: string, body: string, button: string, run: () => void): void {
    this.injector.get(AlertService).show({
      title,
      body,
      rightButtons: [
        { title: 'CANCEL', close: true },
        { title: button, close: true, click: run },
      ],
    });
  }

  /** Clears this member's follows of team mail and folders, not the shared team's state. */
  private clearMySubscriptions(): void {
    const tenant = this.tenantId;
    if (!tenant) return;
    const toaster = this.injector.get(ToasterService);
    forkJoin([this.messagesService.follow.clear(tenant), this.messageFoldersService.follow.clear(tenant)]).subscribe({
      next: ([emails, folders]) =>
        toaster.show({ text: emails.removed + folders.removed > 0 ? ITEM_FOLLOW_SAVED : 'No items in this view.' }),
      error: (error: unknown) => toaster.show({ text: itemFollowErrorMessage(error) }),
    });
  }

  public readAll() {
    const filters = new MessagesFilters();
    filters.applyVirtualFolder(this.sidebarFilterKey);

    this.messagesService.read(filters, true, { tenantItems: [this.stitchItem] });
  }

  /** Clears only the clicked workspace rail row's messages and folders in its mailbox. */
  private clearRailRow(): void {
    const key = this.sidebarFilterKey;
    if (key !== 'flagged' && key !== 'followed' && key !== 'snoozed') return;
    const messages = new MessagesFilters().applyVirtualFolder(key);
    const folders = new MessageFoldersFilters().applyVirtualFolder(key);
    if (key === 'flagged') {
      this.messagesService.flag(messages, false, { tenantItems: [this.stitchItem] });
      this.messageFoldersService.flag(folders, false, { tenantItems: [this.containerStitchItem] });
    } else if (key === 'followed') {
      this.messagesService.followUp(messages, null, { tenantItems: [this.stitchItem] });
      this.messageFoldersService.followUp(folders, null, { tenantItems: [this.containerStitchItem] });
    } else {
      this.messagesService.snooze(messages, null, { tenantItems: [this.stitchItem] });
      this.messageFoldersService.snooze(folders, null, { tenantItems: [this.containerStitchItem] });
    }
  }

  public sendAll() {
    this.messagesService.sendAll({ tenantItems: [this.stitchItem] });
  }

  public override deleteAll(): void {
    const messagesFilter = new MessagesFilters().applyVirtualFolder(this.sidebarFilterKey);
    this.messagesService.delete(messagesFilter, true, undefined, { tenantItems: [this.stitchItem] });

    // Spam contains messages only. Archived can contain both messages and message folders, so its
    // second request uses the equivalent folder filter rather than sweeping an unrelated view.
    if (this.sidebarFilterKey === 'archived') {
      const foldersFilter = new MessageFoldersFilters().applyVirtualFolder(this.sidebarFilterKey);
      this.messageFoldersService.delete(foldersFilter, true, undefined, {
        tenantItems: [this.containerStitchItem],
      });
    }
  }

  protected getCommonParams(): Like<Message | MessageFolder> {
    // A menu opened on a ROW acts on THAT ROW's mailbox, whatever the rail is showing. The first arm
    // below `break`s without calling `super.getCommonParams()`, so its items used to carry no
    // `accountId` at all and fell through to `effectiveTenantId()` — Delete all on a team Spam row
    // named the mailbox on screen (#1075). Seeding here reaches ONLY that arm: the second arm
    // REASSIGNS `params` at the `super` call, so it cannot be double-stamped.
    let params: Like<Message | MessageFolder> = { accountId: this.tenantId };

    switch (this.sidebarFilterKey) {
      case 'assigned_to_me':
      case 'following':
      case 'inbox':
      case 'vip':
      case 'all_folders':
      case 'spam':
      case 'sent':
      case 'scheduled':
      case 'draft':
      case 'bulk':
      case 'other_mail':
        break;
      case 'snoozed':
      case 'archived':
      case 'deleted':
      case 'flagged':
      case 'followed':
        params = super.getCommonParams();
        break;
      default:
        checkExhaustiveness(this.sidebarFilterKey);
    }

    return params;
  }

  protected stitchItemFactory(params: Partial<Message>): Message {
    return new Message({ ...params, folderId: this.selectedContainerStitchItemId });
  }

  protected containerStitchItemFactory(params: Partial<MessageFolder>): MessageFolder {
    return new MessageFolder(params);
  }
}
